import { useState } from 'react';
import PropTypes from 'prop-types';
import Button from '@mui/material/Button';
import Typography from '@mui/material/Typography';
import Card from '@mui/material/Card';
import CardActions from '@mui/material/CardActions';
import CircularProgress from '@mui/material/CircularProgress';
import Avatar from '@mui/material/Avatar';
import PasswordIcon from '@mui/icons-material/Password';

import ib_logo from '../img/ib-circle-logo.png'
import { Notification, useRedirect, Form } from 'react-admin';
import { useNotify } from 'ra-core';
import { Link as RouterLink } from 'react-router-dom';
import Link from '@mui/material/Link';
import { API_ROOT } from '../transport-layer'
import Box from '@mui/material/Box';
import { useClientContext } from '../contexts/client';
import { TextInput } from 'react-admin';


interface FormValues {
    new_password_1?: string;
    new_password_2?: string;
}


const ResetPassword = (props:any) => {
    // Use window.location.hash to get the part after '#'
    const hash = window.location.hash; // e.g. "#/create_password?reset_token=..."
    const queryString = hash.includes('?') ? hash.substring(hash.indexOf('?')) : '';
    const urlParams = new URLSearchParams(queryString);
    const resetToken = urlParams.get('reset_token');
    const [loading, setLoading] = useState(false);
    
    const notify = useNotify();
    const redirect = useRedirect();
    const { state: client } = useClientContext()
    const clientLogo = client.client.logo
    const backgroundImage = client.client.backgroundImage; // Get background image from client context
    const CLIENT = client.client.resourcePrefix
    
    const handleSubmit = (formVals: FormValues) => {
        setLoading(true);
        fetch(`${API_ROOT}/rpc/password_reset`, {
            method: 'POST',
            headers: {
                'Accept': 'application/json',
                'Content-Type': 'application/json'
            },
            body: JSON.stringify({
                ...formVals,
                reset_token: resetToken,
                client: CLIENT
            })
        })  
            .then((e:any) => {
                if (e.status === 200) {
                    return e.json();
                }
                else
                {
                    notify('User not found', { 'type': 'warning'});
                    // eslint-disable-next-line no-throw-literal
                    throw `error with status ${e.status}`;
                }
    
            })
            .then((e) => {
                notify('Password Updated', { 'type': 'info'});
                redirect('/login');
            })
            .catch((e) => {
                notify('Password Update Error', { 'type': 'warning'})
                redirect('/contact');
            })
            .finally(() => {
                setLoading(false);
            });
    };

    const validateUserPWCreation = (values: FormValues) => {
        const errors: FormValues = {};
        if (!values.new_password_1) {
            errors.new_password_1 = 'Required';
        }
        if (!values.new_password_2) {
            errors.new_password_2 = 'Required';
        }
        if (values.new_password_1 !== values.new_password_2) {
            errors.new_password_2 = 'Passwords must match';
        }
        return errors;
    };

    return (resetToken) ? (
                <Form onSubmit={handleSubmit} validate={validateUserPWCreation} noValidate>
                    <Box
                        sx={{
                            display: 'flex',
                            flexDirection: 'column',
                            minHeight: '100vh',
                            alignItems: 'center',
                            justifyContent: 'flex-start',
                            background: `url(${backgroundImage})`,
                            backgroundRepeat: 'no-repeat',
                            backgroundSize: 'cover',
                        }}
                    >
                        <Card sx={{ minWidth: 300, marginTop: '6em' }}>
                            <Box sx={{
                                margin: '1em',
                                display: 'flex',
                                justifyContent: 'center',
                            }}>
                                <img src={clientLogo} alt="Logo" height={'100px'} />
                            </Box>
                            <Box sx={{
                                margin: '1em',
                                display: 'flex',
                                justifyContent: 'center',
                            }}
                            >
                                <Avatar sx={{ bgcolor: 'secondary.main' }}>
                                    <PasswordIcon />
                                </Avatar>
                            </Box>
                            <Box sx={{ padding: '0 1em 1em 1em' }}>
                                <Typography variant="body2" gutterBottom>
                                    Please confirm your new password to complete the process.
                                </Typography>
                                <Box sx={{ marginTop: '1em' }}>
                                    <TextInput
                                        autoFocus
                                        source="new_password_1"
                                        label="NEW PASSWORD"
                                        type="password"
                                        fullWidth
                                        disabled={loading}
                                    />
                                </Box>
                                <Box sx={{ marginTop: '1em' }}>
                                    <TextInput
                                        source="new_password_2"
                                        label="CONFIRM PASSWORD"
                                        type="password"
                                        fullWidth
                                        disabled={loading}
                                    />
                                </Box>
                            </Box>
                            <CardActions sx={{ padding: '0 1em 1em 1em' }}>
                                <Button
                                    variant="contained"
                                    type="submit"
                                    color="primary"
                                    disabled={loading}
                                    fullWidth
                                >
                                    {loading && (
                                        <CircularProgress
                                            size={25}
                                            thickness={2}
                                        />
                                    )}
                                    {'Set Password'}
                                </Button>
                            </CardActions>
                        </Card>
                        <Notification />
                        <div style={{ position: 'absolute', bottom: 10, left: 10 }}>
                            <img src={ib_logo} height='35px' alt='infobate-logo' />
                        </div>
                    </Box>
                </Form>
    ) : (
        <Box
            sx={{
                display: 'flex',
                flexDirection: 'column',
                minHeight: '100vh',
                alignItems: 'center',
                justifyContent: 'flex-start',
                background: `url(${backgroundImage})`,
                backgroundRepeat: 'no-repeat',
                backgroundSize: 'cover',
            }}
        >
            <Card sx={{ minWidth: 300, marginTop: '6em', padding: '2em' }}>
                <Typography variant="body1" gutterBottom>
                    No token found. Please go <Link component={RouterLink} to={'/forgot_password'}>here</Link> to request one.
                </Typography>
            </Card>
            <div style={{ position: 'absolute', bottom: 10, left: 10 }}>
                <img src={ib_logo} height='35px' alt='infobate-logo' />
            </div>
        </Box>
    );
};

ResetPassword.propTypes = {
    authProvider: PropTypes.func,
    previousRoute: PropTypes.string,
};

// We need to put the ThemeProvider decoration in another component
// Because otherwise the useStyles() hook used in ResetPassword won't get
// the right theme
const ResetPasswordWithTheme = (props: any) => (
    <ResetPassword {...props} />
);

export default ResetPasswordWithTheme;